import { ChangeEvent, FC, useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { TextField, PulloverSelect, SwitchIndicator, ElinxNotificationMethod, FieldData } from 'vopay';

import { PaymentFieldDefault } from './types';

type ToggleProps = {
  name: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const Toggle: FC<ToggleProps> = ({ name, checked, onChange }) => {
  return (
    <label htmlFor={`checkbox-${name}`}>
      <SwitchIndicator checked={checked} />
      <input
        style={{ opacity: 0 }}
        id={`checkbox-${name}`}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={(e) => onChange(e)}
      />
    </label>
  );
};

export function useElinxSettingsFields(
  defaultNotificationMethod: ElinxNotificationMethod,
): FieldData<PaymentFieldDefault>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  return useMemo(() => {
    return [
      {
        label: () => t('Fields.sendNotification.label'),
        fieldName: 'SendNotification',
        getInputState: () => 'optional',
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.SendNotification,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<boolean>({});
          return <Toggle checked={fieldData.value} {...fieldData} />;
        },
      },
      {
        label: () => t('Fields.sendReminder.label'),
        fieldName: 'SendReminder',
        getInputState: () => 'optional',
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.SendReminder,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<boolean>({});
          return <Toggle checked={fieldData.value} {...fieldData} />;
        },
      },
      {
        label: () => t('Fields.reminderFrequency.label'),
        fieldName: 'ReminderFrequency',
        getInputState: (ctx) => (ctx.values.SendReminder ? 'required' : 'disabled'),
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.ReminderFrequency,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="" width={'60px'} {...fieldData} />;
        },
        validate: (ctx) => (ctx.values.ReminderFrequency ? Yup.mixed().required() : Yup.mixed()),
      },
      {
        label: () => t('Fields.notificationMethod.label'),
        fieldName: 'NotificationMethod',
        getInputState: (ctx) => (ctx.values.SendNotification ? 'optional' : 'disabled'),
        showField: (values) => values.ProvideOrRequestPaymentInformation === 'request',
        serializeField: (values) => values.NotificationMethod,
        renderField: ({ getFormikData, ctx }) => {
          const fieldData = getFormikData<string>({});
          const options = {
            email: t('notificationOptions.email'),
            sms: t('notificationOptions.sms'),
            ...(defaultNotificationMethod === 'whatsapp' && { whatsapp: t('notificationOptions.whatsapp') }),
          };
          return (
            <PulloverSelect
              {...fieldData}
              onChange={async (value) => {
                ctx.setFieldValue('NotificationMethod', value);
              }}
            >
              {Object.entries(options).map(([value, label]) => (
                <PulloverSelect.Option key={`NotificationMethod-${value}`} value={value}>
                  {label}
                </PulloverSelect.Option>
              ))}
            </PulloverSelect>
          );
        },
      },
    ];
  }, [defaultNotificationMethod]);
}
